import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SideNavbar from './helpers/SideNavbar';
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faExclamationTriangle, faChartBar, faLineChart, faNewspaper, faArrowLeft, faImage, faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FaLinkedin, FaFacebook, FaInstagram } from 'react-icons/fa';
import moment from 'moment-timezone';
import { getAllActivePlatforms, formatPlatformNamesForButton } from '../utils/platformUtils';
import { resizeForInstagram, blobToFile } from '../utils/instagramValidation';


const Updatelatesttrendingnews = () => {
    const { ContentID } = useParams();
    const [postData, setPostData] = useState(null);
    const [updatedContent, setUpdatedContent] = useState("");
    const [imageURL, setImageURL] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [zoomed, setZoomed] = useState(false); // State for zoom effect
    const [selectedFile, setSelectedFile] = useState(null);
    const navigate = useNavigate();
    const AccountType = localStorage.getItem('AccountType');
    const [isPollRequired, setIsPollRequired] = useState(false);
    const [questionData, setQuestionData] = useState({
        question: '',
        options: ['']
    });
    const [isDeleted, setIsDeleted] = useState(false);
    const fileInputRef = useRef(null);
    const [enabledPlatforms, setEnabledPlatforms] = useState([]);
    const [platformsLoading, setPlatformsLoading] = useState(true);
    const [selectedPlatforms, setSelectedPlatforms] = useState([]); // Array of platform codes: ['linkedin', 'facebook', 'instagram']

    useEffect(() => {
        const fetchPostData = async () => {
            try {
                const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/getPost/${ContentID}`;
                const response = await axios.get(apiUrl);
                if (response.data) {
                    setPostData(response.data);
                    setUpdatedContent(response.data.Content || "");
                    setImageURL(response.data.ImageURL || "");
                    if (response.data.pollrequired && response.data.QuestionwithOptions) {
                        setIsPollRequired(response.data.pollrequired);
                        const qData = JSON.parse(response.data.QuestionwithOptions);
                        console.log("Question Data:", qData);

                        // Check if question and options are both empty
                        if (
                            (!qData?.question || qData?.question.trim() === '') &&
                            (!qData?.options || qData?.options.length === 0)
                        ) {
                            setIsDeleted(true); // set deletion flag
                        } else {
                            setQuestionData({
                                question: qData?.question || '',
                                options: qData?.options || ['', '']
                            });
                        }
                    }
                } else {
                    setError("No post data found.");
                }

            } catch (error) {
                console.error("Error fetching post data:", error);
                setError("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };

        const fetchPlatforms = async () => {
            setPlatformsLoading(true);
            const storedAccountType = localStorage.getItem('AccountType');
            
            // For User accounts, automatically use LinkedIn only (no UI needed)
            if (storedAccountType === 'User' || storedAccountType === 'user') {
                setSelectedPlatforms(['linkedin']);
                setEnabledPlatforms([{ name: 'LinkedIn', code: 'linkedin' }]);
                setPlatformsLoading(false);
                return;
            }
            
            // For Page accounts, fetch all active platforms and show selection UI
            try {
                const platforms = await getAllActivePlatforms();
                setEnabledPlatforms(platforms);
                // Default to all platforms selected
                if (platforms.length > 0) {
                    setSelectedPlatforms(platforms.map(p => p.code));
                }
                setPlatformsLoading(false);
            } catch (error) {
                console.error('Error fetching active platforms:', error);
                setPlatformsLoading(false);
            }
        };

        if (ContentID) {
            fetchPostData();
            fetchPlatforms();
        } else {
            setLoading(false);
            setPlatformsLoading(false);
        }
    }, [ContentID, AccountType]);

    const handleImageRemove = () => {
        setImageURL(null);
    };



    const handleUpdate = async () => {
        setLoading(true)
        if (!updatedContent.trim()) {
            return Swal.fire({
                icon: "warning",
                title: "Content is empty!",
                text: "Please enter some text before updating.",
            });
        }

        let questionswithOptions = null;

        const hasValidQuestion = questionData.question.trim() !== '';
        const hasValidOptions = questionData.options.some(opt => opt.trim() !== '');

        if (hasValidQuestion && hasValidOptions) {
            questionswithOptions = {
                question: questionData.question.trim(),
                options: questionData.options.filter(opt => opt.trim() !== '')
            };
        }

        try {
            const formData = new FormData();
            formData.append("ContentID", ContentID);
            formData.append("updatedContent", updatedContent);
            formData.append("removeImage", !imageURL);
            formData.append("pollrequired", isPollRequired);
            formData.append('QuestionwithOptions', JSON.stringify(questionswithOptions));

            // ✅ Resize image for Instagram before appending to FormData
            if (selectedFile) {
                let imageToAppend = selectedFile;
                
                // Check if Instagram is enabled and it's an image file
                const isInstagramEnabled = selectedPlatforms.includes('instagram') || selectedPlatforms.includes('Instagram');
                if (isInstagramEnabled && !isPollRequired && selectedFile.type?.startsWith('image/')) {
                    try {
                        console.log('📐 Resizing image to 1080x1080 (1:1) for Instagram...');
                        const resizedBlob = await resizeForInstagram(selectedFile);
                        
                        // Validate the resized blob
                        if (!resizedBlob || resizedBlob.size === 0) {
                            throw new Error('Resized image is empty or invalid');
                        }
                        
                        console.log('✅ Resized blob size:', resizedBlob.size, 'bytes');
                        imageToAppend = blobToFile(resizedBlob, `instagram-${Date.now()}.jpg`);
                        
                        // Validate the file
                        if (!imageToAppend || imageToAppend.size === 0) {
                            throw new Error('Failed to convert resized blob to file');
                        }
                        
                        console.log('✅ Image resized successfully for Instagram (1080x1080), file size:', imageToAppend.size);
                    } catch (resizeError) {
                        console.error('❌ Error resizing image for Instagram:', resizeError);
                        await Swal.fire({
                            icon: 'error',
                            title: 'Image Resize Failed',
                            html: `
                                <div style="text-align: left;">
                                    <p style="margin-bottom: 10px;"><strong>Failed to resize image for Instagram:</strong></p>
                                    <p style="margin-bottom: 10px; color: #666;">${resizeError.message || 'The image may be corrupted or invalid.'}</p>
                                    <p style="margin-bottom: 10px; color: #666;">Please try:</p>
                                    <ol style="margin-left: 20px; padding-left: 10px; color: #666;">
                                        <li>Selecting a different image</li>
                                        <li>Or uploading an image from your device</li>
                                    </ol>
                                </div>
                            `,
                            showConfirmButton: true,
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#2e3192'
                        });
                        setLoading(false);
                        return;
                    }
                }
                
                formData.append("newImage", imageToAppend); // Use resized image if Instagram is enabled
            }

            const updateResponse = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/updatePostContent`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (updateResponse.status === 200) {
                Swal.fire({
                    position: "center",
                    icon: "success",
                    title: "KnackFeed updated successfully.",
                    showConfirmButton: false,
                    timer: 3000,
                })
                    .then(() => navigate("/KnackFeeds"));
            } else {
                throw new Error(updateResponse.data?.message || "Something went wrong.");
            }
            setLoading(false);
        } catch (error) {
            console.error("❌ Error updating content:", error);
            console.error("❌ Error details:", {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            
            // Provide more specific error message
            let errorMessage = 'Failed to update!';
            let errorDetails = '';
            
            if (error.message?.includes('resize')) {
                errorMessage = 'Failed to resize image for Instagram.';
                errorDetails = 'The image could not be resized to the required format.';
            } else if (error.response?.status === 413) {
                errorMessage = 'Image file too large.';
                errorDetails = 'The image file exceeds the maximum size limit.';
            } else if (error.response?.status === 400) {
                errorMessage = 'Invalid image format.';
                errorDetails = 'The image format is not supported or the file is corrupted.';
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
                errorDetails = error.message || 'Please try again.';
            } else {
                errorDetails = error.message || 'An unexpected error occurred.';
            }
            
            Swal.fire({
                icon: "error",
                title: errorMessage,
                html: errorDetails ? `
                    <div style="text-align: left;">
                        <p style="margin-bottom: 10px; color: #666;">${errorDetails}</p>
                        <p style="color: #666;">Please try again or contact support if the issue persists.</p>
                    </div>
                ` : errorDetails,
                confirmButtonText: 'OK',
                confirmButtonColor: '#2e3192',
                width: '500px'
            });
            setLoading(false);
        }
    };


    const handlePlatformToggle = (platformCode) => {
        if (selectedPlatforms.includes(platformCode)) {
            // Prevent unchecking if it's the last selected platform
            if (selectedPlatforms.length > 1) {
                setSelectedPlatforms(selectedPlatforms.filter(p => p !== platformCode));
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'At Least One Platform Required',
                    text: 'Please select at least one platform to post to.',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#2e3192',
                    timer: 2000
                });
            }
        } else {
            setSelectedPlatforms([...selectedPlatforms, platformCode]);
        }
    };

    const handlePostToLinkedIn = async () => {
        // Validate that at least one platform is selected
        if (!selectedPlatforms || selectedPlatforms.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'No Platform Selected',
                text: 'Please select at least one platform to post to.',
                confirmButtonText: 'OK',
                confirmButtonColor: '#2e3192',
                timer: 2000
            });
            return;
        }

        setLoading(true);
        try {
            const accessToken = localStorage.getItem("AccessToken");
            const tokenID = localStorage.getItem("Tokenid");

            if (!accessToken || !tokenID) {
                setLoading(false);
                return Swal.fire({
                    icon: "error",
                    title: "Missing LinkedIn credentials!",
                    text: "Please re-authenticate before posting to LinkedIn.",
                });
            }

            // Calculate ScheduledTimeAsPerZone in the user's timezone
            // Get user's timezone - use browser's timezone
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const nowInUserTimezone = moment.tz(userTimezone);
            const scheduledTimeAsPerZone = nowInUserTimezone.format('HH:mm:ss');

            // Convert selectedPlatforms array to comma-separated string for query parameter
            const platformsParam = selectedPlatforms.join(',');

            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/postToLinkedInlatestnews?ContentID=${ContentID}&accessToken=${accessToken}&tokenID=${tokenID}&AccountType=${AccountType}&timezone=${encodeURIComponent(userTimezone)}&scheduledTimeAsPerZone=${encodeURIComponent(scheduledTimeAsPerZone)}&platforms=${encodeURIComponent(platformsParam)}`
            );

            if (response.status === 200 && response.data?.message?.includes("successfully")) {
                // Use backend message which already includes the correct platforms
                // Backend returns: "Post successfully published on LinkedIn, Facebook, Instagram!"
                const successTitle = response.data?.message || "Posted successfully!";
                
                Swal.fire({
                    icon: "success",
                    title: successTitle,
                    showConfirmButton: false,
                    timer: 3000,
                }).then(() => navigate("/KnackFeeds"));
            } else {
                throw new Error("LinkedIn did not accept the image or content.");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error posting to LinkedIn:", error);
            
            // Check if it's a limit exceeded error (402)
            if (error.response?.status === 402) {
                Swal.fire({
                    position: 'center',
                    icon: 'warning',
                    title: 'Knack Feeds Limit Reached',
                    text: `You have reached your limit for Knack Feeds. Please try again tomorrow.`,
                    showConfirmButton: true,
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#2e3192',
                    showCancelButton: false
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Failed to post!",
                    text: error.response?.data?.message || error.message || "An error occurred while posting to LinkedIn.",
                });
            }
            setLoading(false);
        }
    };

    return (
        <SideNavbar>
            {loading ? (
                <div className="spinner"></div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : (
                <div className="card shadow-sm p-4 mt-3" style={{ border: "none", backgroundColor: "#fff", borderRadius: "12px" }}>
                    {/* Header matching CreatePostPage */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '24px',
                        paddingBottom: '16px',
                        borderBottom: '1px solid #e0dfdc'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <button
                                onClick={() => navigate('/KnackFeeds')}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    marginRight: '12px',
                                    borderRadius: '4px',
                                    color: '#2e3192',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f3f2ef';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                }}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '18px' }} />
                            </button>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: '#2e3192',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '12px'
                            }}>
                                <FontAwesomeIcon icon={faEdit} style={{ color: 'white', fontSize: '18px' }} />
                            </div>
                            <div>
                                <h2 style={{ 
                                    color: '#000000',
                                    fontWeight: '600',
                                    fontSize: '20px',
                                    margin: '0 0 4px 0'
                                }}>
                                    Edit Knack Feeds
                                </h2>
                                <p style={{ color: '#666666', margin: '0', fontSize: '14px' }}>
                                    Update and modify your Knack Feeds content
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="card-body" style={{ paddingTop: '0' }}>

                        {/* Content Section */}
                        <div style={{ marginBottom: '24px' }}>
                            <textarea
                                placeholder="Enter your Knack Feeds content here..."
                                value={updatedContent}
                                onChange={(e) => {
                                    const content = e.target.value;
                                    if (content.length > 2950) {
                                        Swal.fire({
                                            icon: "warning",
                                            title: "Content Length Exceeded",
                                            text: "The content should not exceed 2950 characters.",
                                        });
                                    } else {
                                        setUpdatedContent(content);
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    border: '1px solid #e0dfdc',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    padding: '16px',
                                    background: '#FFFFFF',
                                    resize: 'vertical',
                                    outline: 'none',
                                    minHeight: '400px',
                                    fontFamily: 'inherit',
                                    lineHeight: '1.5'
                                }}
                            />
                        </div>

                        {/* Display Image Below Text Area */}

                        {imageURL && (
                            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                <img
                                    src={imageURL}
                                    alt="Post Preview"
                                    className="clickable-image"
                                    onClick={() => setZoomed(true)}
                                    style={{ 
                                        maxWidth: '100%', 
                                        height: 'auto', 
                                        borderRadius: '8px',
                                        cursor: 'pointer'
                                    }}
                                />
                                <br />
                                <button 
                                    onClick={handleImageRemove}
                                    style={{
                                        background: '#2e3192',
                                        border: 'none',
                                        color: 'white',
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.3s ease',
                                        marginTop: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#1c1f6d';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#2e3192';
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px' }} />
                                    Remove Image
                                </button>
                            </div>
                        )}
                        {/* Image Upload Field */}
                        {!imageURL && !isPollRequired && (
                            <div className="form-group mt-3 mb-5">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setSelectedFile(file); // Important: store the file for backend
                                            setImageURL(URL.createObjectURL(file)); // For preview
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    title="Upload Image"
                                    style={{
                                        fontSize: '16px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: '#2e3192',
                                        padding: '12px',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '48px',
                                        height: '48px',
                                        minWidth: '48px',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#1a1d5c';
                                        e.target.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#2e3192';
                                        e.target.style.transform = 'scale(1)';
                                    }}
                                >
                                    <FontAwesomeIcon icon={faUpload} />
                                </button>
                            </div>
                        )}

                        {!isDeleted && isPollRequired && (
                            <div className="question-editor">
                                <label>Poll Question:</label>
                                {console.log("Question Data:", questionData)}
                                <input
                                    type="text"
                                    value={questionData.question}
                                    onChange={(e) =>
                                        setQuestionData({ ...questionData, question: e.target.value })
                                    }
                                    placeholder="Enter your question"
                                    className="form-control mb-2"
                                />

                                <label>Options:</label>
                                {questionData.options.map((opt, idx) => (
                                    <div key={idx} className="d-flex align-items-center mb-2">
                                        <input
                                            type="text"
                                            value={opt}
                                            onChange={(e) => {
                                                const newOptions = [...questionData.options];
                                                newOptions[idx] = e.target.value;
                                                setQuestionData({ ...questionData, options: newOptions });
                                            }}
                                            className="form-control me-2"
                                        />
                                        {questionData.options.length > 2 && (
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={() => {
                                                    const newOptions = questionData.options.filter((_, i) => i !== idx);
                                                    setQuestionData({ ...questionData, options: newOptions });
                                                }}
                                            >
                                                <i
                                                    className="fa fa-trash text-white"
                                                    style={{ cursor: "pointer", color: '#fff' }}
                                                    title="Remove Option"

                                                ></i>
                                            </button>
                                        )}
                                    </div>
                                ))}

                                {questionData.options.length < 4 && (

                                    <button style={{ backgroundColor: '#2e3192', marginRight: 10 }} onClick={() =>
                                        setQuestionData({ ...questionData, options: [...questionData.options, ''] })
                                    }> <i
                                        className="fa fa-plus text-white"
                                        style={{ cursor: "pointer" }}
                                        title="Add Option"
                                    ></i></button>
                                )}
                            </div>
                        )}

                        {/* Zoomed Image Modal */}
                        {zoomed && (
                            <div className="image-modal" onClick={() => setZoomed(false)}>
                                <img src={imageURL} alt="Zoomed View" className="zoomed-image" />
                            </div>
                        )}

                        {/* Platform Selection - Only show for Page accounts */}
                        {!platformsLoading && enabledPlatforms.length > 0 && (localStorage.getItem('AccountType') !== 'User' && localStorage.getItem('AccountType') !== 'user') && (
                            <div style={{
                                marginTop: '20px',
                                padding: '16px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                border: '1px solid #e0dfdc'
                            }}>
                                <label style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#2e3192',
                                    marginBottom: '12px',
                                    display: 'block'
                                }}>
                                    Select Platforms to Post To:
                                </label>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '16px',
                                    alignItems: 'center'
                                }}>
                                    {enabledPlatforms.map((platform) => {
                                        const platformIcons = {
                                            linkedin: <FaLinkedin style={{ color: '#0077b5', fontSize: '24px' }} />,
                                            facebook: <FaFacebook style={{ color: '#1877F2', fontSize: '24px' }} />,
                                            instagram: <FaInstagram style={{ color: '#E4405F', fontSize: '24px' }} />
                                        };
                                        const isSelected = selectedPlatforms.includes(platform.code);
                                        
                                        return (
                                            <label
                                                key={platform.code}
                                                title={platform.name}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    padding: '10px',
                                                    borderRadius: '8px',
                                                    backgroundColor: isSelected ? '#e8f0fe' : 'white',
                                                    border: `2px solid ${isSelected ? '#2e3192' : '#e0dfdc'}`,
                                                    transition: 'all 0.2s ease',
                                                    userSelect: 'none',
                                                    minWidth: '48px',
                                                    height: '48px',
                                                    position: 'relative'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = isSelected ? '#d0e7ff' : '#f0f0f0';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = isSelected ? '#e8f0fe' : 'white';
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handlePlatformToggle(platform.code)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '4px',
                                                        right: '4px',
                                                        width: '16px',
                                                        height: '16px',
                                                        cursor: 'pointer',
                                                        accentColor: '#2e3192',
                                                        zIndex: 1
                                                    }}
                                                />
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    {platformIcons[platform.code] || <FaLinkedin style={{ fontSize: '24px' }} />}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                                <small style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginTop: '8px',
                                    color: '#666',
                                    fontSize: '12px',
                                    gap: '8px'
                                }}>
                                    Selected: {selectedPlatforms.length > 0 
                                        ? selectedPlatforms.map((code, index) => {
                                            const platformIcons = {
                                                linkedin: <FaLinkedin key={index} style={{ color: '#0077b5', fontSize: '16px' }} />,
                                                facebook: <FaFacebook key={index} style={{ color: '#1877F2', fontSize: '16px' }} />,
                                                instagram: <FaInstagram key={index} style={{ color: '#E4405F', fontSize: '16px' }} />
                                            };
                                            return platformIcons[code] || null;
                                          })
                                        : 'None'}
                                </small>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div style={{ 
                            display: 'flex', 
                            gap: '8px', 
                            marginTop: '24px',
                            paddingTop: '16px',
                            borderTop: '1px solid #e0dfdc'
                        }}>
                            <button
                                onClick={handleUpdate}
                                style={{
                                    background: '#2e3192',
                                    border: 'none',
                                    color: 'white',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#1c1f6d';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#2e3192';
                                }}
                            >
                                Update
                            </button>
                            <button
                                onClick={handlePostToLinkedIn}
                                disabled={platformsLoading || enabledPlatforms.length === 0 || selectedPlatforms.length === 0}
                                style={{
                                    background: (platformsLoading || enabledPlatforms.length === 0 || selectedPlatforms.length === 0) ? '#cccccc' : '#2e3192',
                                    border: 'none',
                                    color: 'white',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: (platformsLoading || enabledPlatforms.length === 0 || selectedPlatforms.length === 0) ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (!(platformsLoading || enabledPlatforms.length === 0 || selectedPlatforms.length === 0)) {
                                        e.target.style.backgroundColor = '#1c1f6d';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!(platformsLoading || enabledPlatforms.length === 0 || selectedPlatforms.length === 0)) {
                                        e.target.style.backgroundColor = '#2e3192';
                                    }
                                }}
                            >
                                {platformsLoading 
                                    ? 'Loading...' 
                                    : enabledPlatforms.length === 0 
                                        ? 'No Platform Enabled' 
                                        : selectedPlatforms.length === 0
                                            ? 'Select Platform'
                                            : formatPlatformNamesForButton(selectedPlatforms.map(code => {
                                                const platform = enabledPlatforms.find(p => p.code === code);
                                                return platform || { name: code, code };
                                              }))}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </SideNavbar>
    );
};

export default Updatelatesttrendingnews;


